import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminNav, { AdminHeader } from "@/components/AdminNav";
import { Mail, Download } from "lucide-react";
import { toast } from "sonner";

export default function AdminWaitlist() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/waitlist")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load waitlist"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await api.get("/admin/waitlist/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `interstate-pc-waitlist-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="container-tight py-8" data-testid="admin-waitlist-page">
      <AdminNav />
      <AdminHeader title="WAITLIST" subtitle="Pre-launch email signups — not customer accounts" />
      {loading || !data ? (
        <div className="font-mono text-zinc-500">LOADING...</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <Stat label="TOTAL" value={data.total} />
            <Stat label="SUBSCRIBED" value={data.subscribed} color="#22c55e" />
            <Stat label="UNSUBSCRIBED" value={data.unsubscribed} color="#eab308" />
          </div>

          <div className="flex justify-between items-center mb-3">
            <div className="font-mono text-xs tracking-widest text-zinc-400 uppercase">
              Recent signups
            </div>
            <button onClick={exportCsv} disabled={exporting || data.total === 0}
                    data-testid="waitlist-export-csv-btn"
                    className="flex items-center gap-2 bg-[#FF5C00] hover:bg-[#FF7A33] disabled:bg-zinc-800 disabled:text-zinc-600 text-black font-mono text-xs tracking-widest uppercase px-4 py-2 rounded-sm transition-colors">
              <Download className="w-3.5 h-3.5" />
              {exporting ? "Exporting..." : "Export CSV"}
            </button>
          </div>

          <div className="border border-zinc-800 bg-[#0a0a0c] rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase border-b border-zinc-900">
                  <th className="p-3 text-left">Email</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Source</th>
                  <th className="p-3 text-left">Signed Up</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-6 text-center text-zinc-500 font-mono text-xs"
                        data-testid="waitlist-empty">
                      No signups yet.
                    </td>
                  </tr>
                ) : data.items.map((w) => (
                  <tr key={w.id} className="border-b border-zinc-900 hover:bg-black/40"
                      data-testid={`waitlist-row-${w.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-[#FF5C00]" />
                        <span className="font-mono text-xs text-white">{w.email}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border rounded-sm ${
                        w.status === "subscribed"
                          ? "bg-green-500/10 text-green-400 border-green-500/30"
                          : "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                      }`}>{w.status}</span>
                    </td>
                    <td className="p-3 font-mono text-[11px] text-zinc-500">{w.source || "—"}</td>
                    <td className="p-3 font-mono text-[11px] text-zinc-400">
                      {new Date(w.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, color = "#FF5C00" }) {
  return (
    <div className="border border-zinc-800 bg-[#0a0a0c] p-4 rounded-sm">
      <div className="font-mono text-[10px] tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-2xl font-bold mt-2" style={{ color }}>{value}</div>
    </div>
  );
}
